import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "../App";
import { Bell, Check, CheckCheck, X, Briefcase, MapPin, Clock, Loader2, AlertTriangle, Volume2 } from "lucide-react";
import { toast } from "sonner";

// Notification sounds
const notificationSound = new Audio('/sounds/notification.wav');
const urgentSound = new Audio('/sounds/urgent.wav');
notificationSound.volume = 0.5;
urgentSound.volume = 0.7;

// Play notification sound
const playNotificationSound = (isUrgent = false) => {
  try {
    const sound = isUrgent ? urgentSound : notificationSound;
    sound.currentTime = 0;
    sound.play().catch(() => {
      // Ignore autoplay errors
    });
  } catch (e) {
    // Ignore sound errors
  }
};

// Format relative time
const formatRelativeTime = (timestamp) => {
  if (!timestamp) return "";
  const date = new Date(timestamp);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return "Just now";
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

export default function NotificationBell() {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [lastNotificationId, setLastNotificationId] = useState(null);
  const dropdownRef = useRef(null);

  const isProfessional = user && user.user_type === "professional";

  // Fetch sound preference
  useEffect(() => {
    const fetchPrefs = async () => {
      if (!token || !isProfessional) return;
      try {
        const response = await axios.get(`${API}/pro/notification-preferences`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setSoundEnabled(response.data.sound_enabled !== false);
      } catch (e) {
        // Use default (enabled)
      }
    };
    fetchPrefs();
  }, [token, isProfessional]);

  const fetchNotifications = useCallback(async () => {
    if (!token || !isProfessional) return;
    
    try {
      const response = await axios.get(`${API}/pro/notifications?limit=10`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const newNotifications = response.data.notifications || [];
      const newUnreadCount = response.data.unread_count || 0;
      
      // Check for new notifications and play sound
      if (newNotifications.length > 0 && soundEnabled) {
        const latestId = newNotifications[0]?.id;
        if (lastNotificationId && latestId !== lastNotificationId && newUnreadCount > unreadCount) {
          // New notification arrived - play sound
          const isUrgent = newNotifications[0]?.is_urgent || newNotifications[0]?.type === "urgent_lead";
          playNotificationSound(isUrgent);
          
          // Show toast for urgent leads
          if (isUrgent) {
            toast.warning(newNotifications[0].title, {
              description: newNotifications[0].message,
              duration: 10000,
            });
          }
        }
        setLastNotificationId(latestId);
      }
      
      setNotifications(newNotifications);
      setUnreadCount(newUnreadCount);
    } catch (error) {
      console.error("Failed to fetch notifications:", error);
    }
  }, [token, isProfessional, soundEnabled, lastNotificationId, unreadCount]);

  // Fetch notifications on mount and periodically
  useEffect(() => {
    if (!isProfessional) return;
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 30000); // Poll every 30 seconds
    return () => clearInterval(interval);
  }, [fetchNotifications, isProfessional]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleNotificationClick = async (notification) => {
    // Mark as read
    if (!notification.read) {
      try {
        await axios.put(
          `${API}/pro/notifications/${notification.id}/read`,
          {},
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setNotifications(prev => 
          prev.map(n => n.id === notification.id ? { ...n, read: true } : n)
        );
        setUnreadCount(prev => Math.max(0, prev - 1));
      } catch (error) {
        console.error("Failed to mark notification as read:", error);
      }
    }

    // Navigate to the link
    setIsOpen(false);
    if (notification.link) {
      navigate(notification.link);
    }
  };

  const handleMarkAllRead = async () => {
    setLoading(true);
    try {
      await axios.put(
        `${API}/pro/notifications/read-all`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
      toast.success("All notifications marked as read");
    } catch (error) {
      toast.error("Failed to mark notifications as read");
    } finally {
      setLoading(false);
    }
  };

  // Only show for professionals
  if (!isProfessional) {
    return null;
  }

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Bell Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 rounded-lg hover:bg-white/10 transition-colors"
        data-testid="notification-bell"
        aria-label="Notifications"
      >
        <Bell className="w-5 h-5 text-white" />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] flex items-center justify-center bg-red-500 text-white text-[10px] font-bold rounded-full px-1">
            {unreadCount > 99 ? "99+" : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute right-0 top-full mt-2 w-80 sm:w-96 bg-white rounded-xl shadow-2xl border border-slate-200 overflow-hidden z-50 animate-in fade-in slide-in-from-top-2 duration-200">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100 bg-slate-50">
            <h3 className="font-semibold text-[#1e3a5f]">Notifications</h3>
            {unreadCount > 0 && (
              <button
                onClick={handleMarkAllRead}
                disabled={loading}
                className="flex items-center gap-1 text-xs text-[#1e3a5f] hover:text-[#d4a853] transition-colors disabled:opacity-50"
              >
                {loading ? (
                  <Loader2 className="w-3 h-3 animate-spin" />
                ) : (
                  <CheckCheck className="w-3 h-3" />
                )}
                Mark all read
              </button>
            )}
          </div>

          {/* Notifications List */}
          <div className="max-h-80 overflow-y-auto">
            {notifications.length === 0 ? (
              <div className="py-8 text-center">
                <Bell className="w-10 h-10 text-slate-300 mx-auto mb-2" />
                <p className="text-slate-500 text-sm">No notifications yet</p>
                <p className="text-slate-400 text-xs mt-1">You'll be notified when new leads arrive</p>
              </div>
            ) : (
              notifications.map((notification) => {
                const isUrgent = notification.is_urgent || notification.type === "urgent_lead";
                return (
                <button
                  key={notification.id}
                  onClick={() => handleNotificationClick(notification)}
                  className={`w-full text-left px-4 py-3 border-b border-slate-100 last:border-0 hover:bg-slate-50 transition-colors ${
                    isUrgent && !notification.read 
                      ? "bg-red-50 border-l-4 border-l-red-500" 
                      : !notification.read 
                        ? "bg-[#d4a853]/5" 
                        : ""
                  }`}
                  data-testid={`notification-${notification.id}`}
                >
                  <div className="flex gap-3">
                    {/* Icon */}
                    <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${
                      isUrgent
                        ? "bg-red-100 text-red-600 animate-pulse"
                        : notification.type === "new_lead" 
                          ? "bg-emerald-100 text-emerald-600" 
                          : "bg-blue-100 text-blue-600"
                    }`}>
                      {isUrgent ? <AlertTriangle className="w-4 h-4" /> : <Briefcase className="w-4 h-4" />}
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <p className={`text-sm ${!notification.read ? "font-semibold text-[#1e3a5f]" : "text-slate-700"}`}>
                          {notification.title}
                        </p>
                        {!notification.read && (
                          <span className="w-2 h-2 bg-[#d4a853] rounded-full flex-shrink-0 mt-1.5"></span>
                        )}
                      </div>
                      <p className="text-xs text-slate-500 truncate mt-0.5">
                        {notification.message}
                      </p>
                      <div className="flex items-center gap-3 mt-1">
                        {notification.location && (
                          <span className="flex items-center gap-1 text-[10px] text-slate-400">
                            <MapPin className="w-3 h-3" />
                            {notification.location}
                          </span>
                        )}
                        <span className="flex items-center gap-1 text-[10px] text-slate-400">
                          <Clock className="w-3 h-3" />
                          {formatRelativeTime(notification.created_at)}
                        </span>
                      </div>
                    </div>
                  </div>
                </button>
              );})
            )}
          </div>

          {/* Footer */}
          {notifications.length > 0 && (
            <div className="px-4 py-2 border-t border-slate-100 bg-slate-50">
              <button
                onClick={() => {
                  setIsOpen(false);
                  navigate("/pro/leads");
                }}
                className="w-full text-center text-xs text-[#1e3a5f] hover:text-[#d4a853] font-medium py-1"
              >
                View All Leads
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
